import React, { useState } from "react";
import { Button, Typography } from "antd";
import {
  ArrowRightOutlined,
  CalendarOutlined,
  EyeOutlined,
  FileOutlined,
  PhoneOutlined,
  PlayCircleOutlined,
} from "@ant-design/icons";
import { useCurrentApplication } from "../../../Context/CurrentApplicationContext/CurrentApplicationContext";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useJobApplicationActions } from "../../../Hooks/useJobApplicationActions";
import ResumeUploader from "./ResumeUploader/ResumeUploader";
import { useNavigate } from "react-router-dom";
import { getLastPendingRoundInfo } from "../../../Utils/job-application.utils";
import { useAppContext } from "../../../Context/AppContext/AppContext";
import { openNotification } from "../../../Components/Notification/Index";
import { qualifymeSupportEmail } from "../../../Utils/constants";
import ResumeViewerDrawer from "../../../Components/ResumeViewerDrawer/ResumeViewerDrawer";

dayjs.extend(relativeTime);

const formatCTC = (ctc: number | undefined, currency: string | undefined) => {
  if (!ctc) return null;
  const currencySymbol =
    currency === "USD"
      ? "$"
      : currency === "EUR"
        ? "€"
        : currency === "GBP"
          ? "£"
          : "₹";

  // Format in lakhs for INR, otherwise show as-is with K/M suffix
  if (!currency || currency === "INR") {
    if (ctc >= 10000000) {
      return `${currencySymbol}${(ctc / 10000000).toFixed(1)} Cr`;
    }
    if (ctc >= 100000) {
      return `${currencySymbol}${(ctc / 100000).toFixed(1)} LPA`;
    }
    return `${currencySymbol}${ctc.toLocaleString()}`;
  }

  // For other currencies
  if (ctc >= 1000000) {
    return `${currencySymbol}${(ctc / 1000000).toFixed(1)}M`;
  }
  if (ctc >= 1000) {
    return `${currencySymbol}${(ctc / 1000).toFixed(0)}K`;
  }
  return `${currencySymbol}${ctc.toLocaleString()}`;
};

const formatNoticePeriod = (days: number | undefined) => {
  if (!days) return null;
  if (days === 0) return "Immediate";
  if (days <= 15) return `${days} days`;
  if (days <= 30) return "1 month";
  if (days <= 60) return "2 months";
  if (days <= 90) return "3 months";
  return `${Math.round(days / 30)} months`;
};

interface CTAButtonProps {
  text: string;
  icon: React.ReactNode;
  action: "apply" | "upload-resume" | "assessment" | "interview" | null;
}

const ApplicationJourneyCTA: React.FC = () => {
  const { organization } = useAppContext();
  const {
    jobPost,
    jobApplication,
    userProfile,
    currentView,
    interviewSchedule,
    interviewSessions,
    rounds,
    handleViewChange,
  } = useCurrentApplication();
  const { handleApply } = useJobApplicationActions();
  const navigate = useNavigate();

  const [isResumeUploaderOpen, setIsResumeUploaderOpen] = useState(false);
  const [isResumeViewerOpen, setIsResumeViewerOpen] = useState(false);

  const handleUploadResumeClick = () => {
    setIsResumeUploaderOpen(true);
  };

  const handleContactSupportClick = () => {
    const email = organization?.supportEmail ?? qualifymeSupportEmail;
    const subject = "Support Request";
    const bccQualifyMe = organization?.supportEmail
      ? qualifymeSupportEmail
      : undefined;

    const mailtoParams = new URLSearchParams({ subject });
    if (bccQualifyMe) {
      mailtoParams.append("bcc", bccQualifyMe);
    }
    const mailtoLink = `mailto:${email}?${mailtoParams.toString()}`;

    let mailClientLikelyOpened = false;

    const handleBlur = () => {
      mailClientLikelyOpened = true;
      window.removeEventListener("blur", handleBlur);
    };

    window.addEventListener("blur", handleBlur);

    window.location.href = mailtoLink;

    setTimeout(() => {
      window.removeEventListener("blur", handleBlur);

      if (!mailClientLikelyOpened) {
        const gmailParams = new URLSearchParams({
          view: "cm",
          to: email,
          su: subject,
        });
        if (bccQualifyMe) {
          gmailParams.append("bcc", bccQualifyMe);
        }
        const fallbackGmailLink = `https://mail.google.com/mail/?${gmailParams.toString()}`;
        const newTab = window.open(fallbackGmailLink, "_blank");

        if (newTab) {
          newTab.focus();
        } else {
          openNotification(
            "warning",
            "Blocker detected",
            `Please allow pop-ups for this site or visit email app  and send email to ${email}.`,
          );
        }
      }
    }, 750);
  };

  if (!jobPost) {
    return null;
  }

  const {
    position,
    numberOfApplications,
    createdAt,
    ctc,
    currency,
    noticePeriod,
  } = jobPost;

  const getCTAButtonProps = (): CTAButtonProps | null => {
    if (!jobApplication) {
      return {
        text: "Apply Now",
        icon: <ArrowRightOutlined />,
        action: "apply",
      };
    } else if (
      jobApplication &&
      jobApplication.status === "applied" &&
      !jobApplication.profileMatchStatus &&
      !jobApplication.profileId
    ) {
      return {
        text: "Upload Resume",
        icon: <FileOutlined />,
        action: "upload-resume",
      };
    } else if (
      jobApplication &&
      rounds.length > 0 &&
      interviewSessions.length > 0 &&
      jobApplication.status === "applied" &&
      jobApplication.questionnaireStatus === "pending"
    ) {
      return {
        text: `Start Assessment`,
        icon: <PlayCircleOutlined />,
        action: "assessment",
      };
    } else if (
      jobPost &&
      jobPost.onlineInterview &&
      jobApplication &&
      jobApplication.status === "interview"
    ) {
      if (!interviewSchedule && !jobApplication.interviewSchedule) {
        return {
          text: "Select Slot",
          icon: <CalendarOutlined />,
          action: "interview",
        };
      } else {
        return {
          text: "View Interview",
          icon: <EyeOutlined />,
          action: "interview",
        };
      }
    }
    return null;
  };

  const handleViewResume = () => {
    setIsResumeViewerOpen(!isResumeViewerOpen);
  };

  const handleAssessmentClick = () => {
    console.log("Handling Assessment Click");
    if (!jobPost) {
      return;
    }

    const { sessionId } = getLastPendingRoundInfo(interviewSessions, rounds);
    console.log("Session ID for Assessment:", sessionId);

    if (!sessionId) {
      return;
    }

    const pendingSession = interviewSessions.find((s) => s._id === sessionId);
    console.log("Pending Session:", pendingSession);
    const round = rounds.find(
      (r) => r.round._id === pendingSession?.interviewRoundId,
    );

    if (round?.round.answerType === "simulation") {
      navigate(`/${jobPost.slug}/simulation?sessionId=${sessionId}`);
    } else {
      navigate(`/${jobPost.slug}/interview-round?sessionId=${sessionId}`);
    }
  };

  const handleCTAAction = async (
    action: "apply" | "upload-resume" | "assessment" | "interview" | null,
  ) => {
    console.log("CTA Action Triggered:", action);
    if (!action) {
      return;
    }
    switch (action) {
      case "apply":
        await handleApply();
        break;
      case "upload-resume":
        handleUploadResumeClick();
        break;
      case "assessment":
        handleAssessmentClick();
        break;
      case "interview":
        handleViewChange("interview");
        break;
    }
  };

  const ctaButtonProps = getCTAButtonProps();

  return (
    <div className="job-posts-details__sidebar-apply-card">
      <div className="job-posts-details__sidebar-apply-card-glow" />
      <div className="job-posts-details__sidebar-apply-stats">
        <div className="job-posts-details__sidebar-apply-stats-left">
          <div className="job-posts-details__sidebar-pulse" />
          <Typography.Text>{numberOfApplications} Applicants</Typography.Text>
        </div>
        {/* <Typography.Text
          type="secondary"
          className="job-posts-details__sidebar-apply-stats-meta"
        >
          {dayjs(createdAt).fromNow()}
        </Typography.Text> */}
      </div>
      <div className="job-posts-details__sidebar-apply-rows">
        <div className="job-posts-details__sidebar-apply-row">
          <Typography.Text type="secondary">Position</Typography.Text>
          <Typography.Text>{position}</Typography.Text>
        </div>
        <div className="job-posts-details__sidebar-apply-row">
          <Typography.Text type="secondary">Posted</Typography.Text>
          <Typography.Text>{dayjs(createdAt).fromNow()}</Typography.Text>
        </div>
        {ctc && currency && (
          <div className="job-posts-details__sidebar-apply-row">
            <Typography.Text type="secondary">CTC</Typography.Text>
            <Typography.Text>{formatCTC(ctc, currency)}</Typography.Text>
          </div>
        )}

        {noticePeriod && (
          <div className="job-posts-details__sidebar-apply-row">
            <Typography.Text type="secondary">
              Notice Period / Availability to Join
            </Typography.Text>
            <Typography.Text>
              {formatNoticePeriod(noticePeriod)}
            </Typography.Text>
          </div>
        )}

        {/* <div className="job-posts-details__sidebar-apply-row">
          <span className="job-posts-details__sidebar-apply-label">
            Referrals available
          </span>
          <span className="job-posts-details__sidebar-apply-value job-posts-details__sidebar-apply-value--primary">
            Yes (2)
          </span>
        </div> */}
      </div>
      <div className="job-posts-details__sidebar-apply-btn-group">
        {userProfile && (
          <Button
            block
            type="default"
            onClick={handleViewResume}
            icon={<FileOutlined />}
            iconPosition="end"
          >
            View Resume
          </Button>
        )}
        {ctaButtonProps && (
          <Button
            block
            type="primary"
            onClick={() => handleCTAAction(ctaButtonProps.action)}
            icon={ctaButtonProps.icon}
            iconPosition="end"
          >
            {ctaButtonProps.text}
          </Button>
        )}

        {currentView === "result" && (
          <Button
            block
            type="default"
            className="job-posts-details__sidebar-resume-btn"
            onClick={handleContactSupportClick}
            icon={<PhoneOutlined />}
            iconPosition="end"
          >
            Contact Support
          </Button>
        )}
      </div>

      <ResumeUploader
        isOpen={isResumeUploaderOpen}
        setIsOpen={setIsResumeUploaderOpen}
      />

      <ResumeViewerDrawer
        open={isResumeViewerOpen}
        onClose={handleViewResume}
        profileId={userProfile?._id}
      />
    </div>
  );
};

export default ApplicationJourneyCTA;
